import { apiClient } from "@/lib/apiClient";
import { getAppointmentRegistry } from "@/lib/appointment/appointmentRegistrySingleton";
import type { AppointmentRecord } from "@/lib/types/appointment";
import type { GoodsCategory, CategoryGoodItem } from "@/lib/types/goods";
import { normalizeAppointmentForApi } from "@/lib/services/appointmentPayload";
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton";
/**
 * Fetches and merges the latest appointment details for a given appointment ID.
 * Loads details for the modal dialog.
 */
export async function fetchAppointmentDetails(id: number): Promise<AppointmentRecord | null> {
  const { data: raw } = await apiClient.get(
    `/api/v1/record/${id}?include_acceptance_free=true`
  );

  const registry = getAppointmentRegistry();
  // ➊ add if missing, otherwise deep-merge
  if (!registry.getAppointmentByIdFull(String(id))) {
    registry.addAppointment(raw);
  } else {
    registry.merge(raw);
  }

  // React Query v5: data must not be undefined
  return (registry.getAppointmentById(String(id)) ?? null) as AppointmentRecord | null;
}

/**
 * Every message about this visit, read fresh from the central ledger (the modal's
 * Messages tab). Not part of the details: those are cached against record changes
 * only, so messages riding on them went stale or stuck as "unavailable".
 */
export async function fetchAppointmentMessages(id: number): Promise<NonNullable<AppointmentRecord["messages"]>> {
  const { data } = await apiClient.get(`/api/v1/record/${id}/messages`);
  return data;
}

/**
 * Fetches and merges payment info for a given appointment ID.
 * Loads payment info for the modal dialog.
 */
export async function fetchAppointmentPaymentInfo(id: number): Promise<AppointmentRecord> {
  // Fetch payment info from the API
  const { data } = await apiClient.get(`/api/v1/company/sale/${id}`);
  const paymentData = data.data;

  // Get the registry and the appointment
  const registry = getAppointmentRegistry();
  const appointment = registry.getAppointmentByIdFull(String(id));
  if (!appointment) throw new Error("Appointment not found in registry");

  // Merge payment info into the registry
  registry.merge({
    id: appointment.id,
    kkm_state: paymentData.kkm_state,
    payment_methods: paymentData.payment_methods,
    payment_summary: paymentData.state?.payment_summary ?? null,
  });

  // Return the updated appointment as AppointmentRecord
  return registry.getAppointmentById(String(id)) as AppointmentRecord;
}

/**
 * Fetches and merges consumables info for a given appointment ID.
 * Loads consumables info for the modal dialog.
 */
export async function fetchAppointmentConsumables(id: number): Promise<any> {
  // Always make all four requests in parallel; the card context (storage
  // plan ST8) is extra — an older backend without it leaves the tab as it was
  const [storageRes, techCardsRes, recordedRes, contextRes] = await Promise.all([
    apiClient.get(`/api/v1/storages`),
    apiClient.get(`/api/v1/technological_cards`),
    apiClient.get(`/api/v1/technological_cards/record_consumables/${id}`),
    apiClient.get(`/api/v1/technological_cards/record_context/${id}`).catch(() => ({ data: {} })),
  ]);

  // Keep recorded consumables grouped by service, preserving service_id
  const recordedData = Array.isArray(recordedRes.data?.data)
    ? recordedRes.data.data.flatMap((service: any) =>
      Array.isArray(service.consumables)
        ? service.consumables.map((consumable: any) => ({
          ...consumable,
          service_id: service.service_id || consumable.service_id
        }))
        : []
    )
    : [];

  const consumables = {
    storage: storageRes.data,
    technological_cards: Array.isArray(techCardsRes.data?.data) ? techCardsRes.data.data : [],
    recorded: recordedData,
    // service id → the card it follows; "good:storage" → the shelf now
    service_cards: (contextRes.data?.service_cards ?? {}) as Record<string, number>,
    stock: (contextRes.data?.stock ?? {}) as Record<string, { on_hand: number; minimum: number | null; unit: string }>,
  };

  return consumables;
}

/**
 * Fetches simplified consumables for a specific record ID.
 * Used for read-only display in visit history details.
 * @param recordId The record ID to fetch consumables for.
 * @returns Array of { service_id, title, amount, unit, price } for each consumable.
 */
export async function fetchRecordConsumables(recordId: number): Promise<{
  service_id: number;
  title: string;
  amount: number;
  unit: string;
  price: number;
}[]> {
  const { data } = await apiClient.get(`/api/v1/technological_cards/record_consumables/${recordId}`);

  // Map consumables with service_id preserved for grouping
  const consumables: { service_id: number; title: string; amount: number; unit: string; price: number }[] = [];

  if (Array.isArray(data?.data)) {
    for (const service of data.data) {
      if (Array.isArray(service.consumables)) {
        for (const c of service.consumables) {
          consumables.push({
            service_id: service.service_id,
            title: c.good?.title || `Good #${c.good_id}`,
            amount: Math.abs(c.amount) || 1,
            unit: c.good?.unit || 'pcs.',
            price: c.price || 0,
          });
        }
      }
    }
  }

  return consumables;
}

/**
 * Fetch available services for a given company and master.
 * Returns an array of { id, title, price_min, price_max, length } for use in the StatusSection dropdown.
 * Prices and length are THIS master's (per-master price / duration), else the service's.
 */
export async function fetchAvailableServices(
  masterId: number
): Promise<{ id: number; title: string; price_min?: number; price_max?: number; length?: number }[]> {
  const { data } = await apiClient.get(
    `/api/v1/company/master/${masterId}/services`
  );
  if (!data || !Array.isArray(data.data)) return [];
  const services = data.data.map((item: any) => ({
    id: item.id,
    title: item.title,
    price_min: item.price_min,
    price_max: item.price_max,
    length: item.length,
  }));
  // Keep the journal-bookable set in the registry too: drag & drop needs to answer
  // "can this master take this appointment?" synchronously, mid-gesture.
  getServiceRegistry().mergeMasterJournalServices(
    masterId,
    services.map((s: { id: number }) => s.id)
  );
  return services;
}

/**
 * Saves the current appointment record by posting it to the backend.
 * @param appointment The appointment object to save.
 * @returns The API response.
 */
export async function saveAppointmentRecord(appointment: AppointmentRecord): Promise<any> {
  // Normalize datetimes to explicit UTC instants before sending to the backend.
  const payload = normalizeAppointmentForApi(appointment);
  const { data } = await apiClient.post("/api/v1/admin-record/save", payload);
  return data;
}

/**
 * Edit the current appointment record by posting it to the backend.
 * @param appointment The appointment object to save.
 * @returns The API response.
 */
export async function editAppointmentRecord(appointment: AppointmentRecord): Promise<any> {
  // Normalize datetimes to explicit UTC instants before sending to the backend.
  const payload = normalizeAppointmentForApi(appointment);
  const { data } = await apiClient.post("/api/v1/admin-record/edit", payload);
  return data;
}

/**
 * Fetches all goods categories with their goods count.
 * Endpoint: GET /company/goods/categories
 */
export async function fetchGoodsCategories(): Promise<GoodsCategory[]> {
  const { data } = await apiClient.get("/api/v1/company/goods/categories");
  return Array.isArray(data?.categories) ? data.categories : [];
}

/**
 * Fetches goods for a specific category.
 * Endpoint: GET /company/goods/categories/{category_id}/items
 */
export async function fetchCategoryGoods(categoryId: number): Promise<CategoryGoodItem[]> {
  const { data } = await apiClient.get(`/api/v1/company/goods/categories/${categoryId}/items`);
  return Array.isArray(data?.items) ? data.items : [];
}

